import { Input } from "./ui/input";
import { Check, X, Music, Music2, Clock, Send, Film, Smartphone } from "lucide-react";

export const DEFAULT_SPEC = { posting_required: false, content_to_send: "edited", duration_min: 15, duration_max: 60, music: "allowed", orientation: "vertical" };

const CONTENT = { raw: "Raw footage", edited: "Edited video", both: "Edited + raw footage" };
const MUSIC = { allowed: "Any music", none: "No music", brand_provided: "Brand-provided track" };
const ORIENT = { vertical: "Vertical 9:16", horizontal: "Horizontal 16:9", square: "Square 1:1", any: "Any" };

const pill = (on) => `px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "border-nexus-blue bg-nexus-blue/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`;

export function DeliverableSpecEditor({ spec, onChange }) {
  const s = { ...DEFAULT_SPEC, ...(spec || {}) };
  const set = (k, v) => onChange({ ...s, [k]: v });
  return (
    <div className="space-y-4" data-testid="deliverable-spec-editor">
      <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Film className="h-3.5 w-3.5" /> Deliverable spec</div>
      <div>
        <div className="text-[11px] text-zinc-500 mb-1.5">Posting to the creator's own channels</div>
        <div className="flex gap-2">
          <button type="button" onClick={() => set("posting_required", true)} className={pill(s.posting_required)} data-testid="spec-posting-yes">Required</button>
          <button type="button" onClick={() => set("posting_required", false)} className={pill(!s.posting_required)} data-testid="spec-posting-no">Not required</button>
        </div>
      </div>
      <div>
        <div className="text-[11px] text-zinc-500 mb-1.5">Content to send</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CONTENT).map(([k, v]) => <button key={k} type="button" onClick={() => set("content_to_send", k)} className={pill(s.content_to_send === k)} data-testid={`spec-content-${k}`}>{v}</button>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] text-zinc-500 mb-1.5">Duration (seconds)</div>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} value={s.duration_min ?? ""} onChange={(e) => set("duration_min", e.target.value)} className="bg-white/[0.02] border-white/10 text-white w-24" data-testid="spec-duration-min" />
            <span className="text-zinc-500">–</span>
            <Input type="number" min={1} value={s.duration_max ?? ""} onChange={(e) => set("duration_max", e.target.value)} className="bg-white/[0.02] border-white/10 text-white w-24" data-testid="spec-duration-max" />
            <span className="text-xs text-zinc-500">sec</span>
          </div>
        </div>
        <div>
          <div className="text-[11px] text-zinc-500 mb-1.5">Orientation</div>
          <select value={s.orientation} onChange={(e) => set("orientation", e.target.value)} className="h-10 w-full rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="spec-orientation">
            {Object.entries(ORIENT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="text-[11px] text-zinc-500 mb-1.5">Music</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(MUSIC).map(([k, v]) => <button key={k} type="button" onClick={() => set("music", k)} className={pill(s.music === k)} data-testid={`spec-music-${k}`}>{v}</button>)}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-white/5 last:border-0" data-testid={testId}>
      <div className="flex items-center gap-2 text-sm text-zinc-400"><Icon className="h-4 w-4 text-zinc-500" /> {label}</div>
      <div className={`text-sm font-semibold text-right inline-flex items-center gap-1.5 ${tone || "text-white"}`}>{value}</div>
    </div>
  );
}

export function DeliverableSpecTable({ spec }) {
  if (!spec) return null;
  const dur = spec.duration_min && spec.duration_max ? `${spec.duration_min} – ${spec.duration_max} sec` : spec.duration_max ? `up to ${spec.duration_max} sec` : spec.duration_min ? `${spec.duration_min}+ sec` : "Flexible";
  return (
    <div data-testid="deliverable-spec-table">
      <Row icon={Send} label="Posting" testId="spec-row-posting"
        value={spec.posting_required ? <><Check className="h-3.5 w-3.5" /> Required</> : <><X className="h-3.5 w-3.5" /> Not required</>}
        tone={spec.posting_required ? "text-emerald-300" : "text-nexus-pink"} />
      <Row icon={Film} label="Content to send" value={CONTENT[spec.content_to_send] || "Edited video"} testId="spec-row-content" />
      <Row icon={Clock} label="Duration" value={dur} testId="spec-row-duration" />
      {spec.orientation && spec.orientation !== "any" && <Row icon={Smartphone} label="Format" value={ORIENT[spec.orientation]} testId="spec-row-orientation" />}
      <Row icon={spec.music === "none" ? Music2 : Music} label="Music" value={MUSIC[spec.music] || "Any music"} tone={spec.music === "none" ? "text-zinc-300" : "text-white"} testId="spec-row-music" />
    </div>
  );
}
